import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import {
    Plus, Trash2, ArrowLeft, BookOpen, Play,
    Search, MoreVertical, GripVertical, Edit3
} from 'lucide-react'
import { useToast } from '../hooks/useToast'
import { supabase } from '../services/supabase'
import Button from '../components/Button'
import ModalModulo from '../components/ModalModulo'
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    type DragEndEvent
} from '@dnd-kit/core'
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

interface Curso {
    id: string
    titulo: string
    descricao: string | null
}

interface Modulo {
    id: string
    titulo: string
    descricao: string | null
    ordem_exibicao: number
    created_at: string
    updated_at: string
    total_aulas: number
}

interface SortableModuloItemProps {
    modulo: Modulo
    navigate: (path: string) => void
    cursoId: string
    activeModuloId?: string | null
    onEdit: (modulo: Modulo) => void
    onDelete: (modulo: Modulo) => void
    openDropdown: string | null
    setOpenDropdown: (id: string | null) => void
}

function SortableModuloItem({ 
    modulo, 
    navigate, 
    cursoId, 
    activeModuloId,
    onEdit,
    onDelete,
    openDropdown,
    setOpenDropdown
}: SortableModuloItemProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: modulo.id })

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1
    }

    const isActive = activeModuloId === modulo.id

    const containerStyle: React.CSSProperties = {
        ...style,
        ...(isActive && {
            borderLeftColor: 'rgb(235, 106, 59)',
            borderLeftWidth: '4px',
            borderLeftStyle: 'solid' as const
        })
    }

    return (
        <div
            ref={setNodeRef}
            style={containerStyle}
            className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-all relative`}
        >
            <div className="p-6">
                <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                        {/* Handle de arrastar */}
                        <button
                            {...attributes}
                            {...listeners}
                            className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                        >
                            <GripVertical className="text-gray-400 dark:text-gray-500" size={20} />
                        </button>

                        <div className="flex-shrink-0 w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                            <span className="text-blue-600 dark:text-blue-400 font-semibold text-lg">
                                {modulo.ordem_exibicao}
                            </span>
                        </div>
                        <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                                {modulo.titulo}
                            </h3>
                            {modulo.descricao && (
                                <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">
                                    {modulo.descricao}
                                </p>
                            )}
                            <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                                <div className="flex items-center gap-1">
                                    <Play className="h-4 w-4" />
                                    <span>{modulo.total_aulas} aulas</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <BookOpen className="h-4 w-4" />
                                    <span>Módulo {modulo.ordem_exibicao}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <Button
                            variant="btn_secondary"
                            size="sm"
                            onClick={() => navigate(`/admin/cursos/${cursoId}/modulos/${modulo.id}/aulas`)}
                            className="flex items-center gap-2"
                        >
                            <Play className="h-4 w-4" />
                            Gerenciar Aulas
                        </Button>

                        <div className="relative">
                            <Button
                                variant="btn_secondary"
                                size="sm"
                                className="p-2"
                                onClick={(e) => {
                                    e?.stopPropagation()
                                    setOpenDropdown(openDropdown === modulo.id ? null : modulo.id)
                                }}
                            >
                                <MoreVertical className="h-4 w-4" />
                            </Button>

                            {/* Dropdown Menu */}
                            {openDropdown === modulo.id && (
                                <div
                                    className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-[9999]"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                    }}
                                >
                                    <div className="py-1">
                                        <button
                                            onMouseDown={(e) => {
                                                e.preventDefault()
                                                e.stopPropagation()
                                                onEdit(modulo)
                                                setOpenDropdown(null)
                                            }}
                                            onClick={(e) => {
                                                e.preventDefault()
                                                e.stopPropagation()
                                                onEdit(modulo)
                                                setOpenDropdown(null)
                                            }}
                                            className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 cursor-pointer"
                                        >
                                            <Edit3 className="h-4 w-4" />
                                            Editar Módulo
                                        </button>
                                        <button
                                            onMouseDown={(e) => {
                                                e.preventDefault()
                                                e.stopPropagation()
                                                onDelete(modulo)
                                                setOpenDropdown(null)
                                            }}
                                            onClick={(e) => {
                                                e.preventDefault()
                                                e.stopPropagation()
                                                onDelete(modulo)
                                                setOpenDropdown(null)
                                            }}
                                            className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2 cursor-pointer"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                            Excluir Módulo
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default function AdminModulos() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const location = useLocation()
    const { showSuccess, showError } = useToast()
    const [curso, setCurso] = useState<Curso | null>(null)
    const [modulos, setModulos] = useState<Modulo[]>([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [editingModulo, setEditingModulo] = useState<Modulo | null>(null)
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
    const [moduloToDelete, setModuloToDelete] = useState<Modulo | null>(null)
    const [openDropdown, setOpenDropdown] = useState<string | null>(null)
    
    // Extrair moduloId da URL se estiver presente (quando voltamos da página de aulas)
    const getActiveModuloId = (): string | null => {
        // Verificar se há moduloId no estado da navegação
        const state = location.state as { moduloId?: string } | null
        if (state?.moduloId) {
            return state.moduloId
        }
        
        // Verificar se há moduloId na URL (caso tenha sido passado como query param)
        const searchParams = new URLSearchParams(location.search)
        const moduloIdFromQuery = searchParams.get('moduloId')
        if (moduloIdFromQuery) {
            return moduloIdFromQuery
        }
        
        return null
    }
    
    const activeModuloId = getActiveModuloId()

    // Configurar sensores para drag and drop
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    )

    // Handler para quando o drag termina
    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event

        if (!over || active.id === over.id) {
            return
        }

        const oldIndex = modulos.findIndex(modulo => modulo.id === active.id)
        const newIndex = modulos.findIndex(modulo => modulo.id === over.id)

        if (oldIndex === -1 || newIndex === -1) {
            return
        }

        // Reordenar a lista localmente
        const newModulos = arrayMove(modulos, oldIndex, newIndex)
        setModulos(newModulos)

        // Atualizar ordem_exibicao no banco de dados
        try {
            const updates = newModulos.map((modulo, index) => ({
                id: modulo.id,
                ordem_exibicao: index + 1
            }))

            // Executar atualizações em lote
            for (const update of updates) {
                const { error } = await supabase
                    .from('modulos')
                    .update({ ordem_exibicao: update.ordem_exibicao })
                    .eq('id', update.id)

                if (error) {
                    console.error('Erro ao atualizar ordem:', error)
                    throw error
                }
            }

            showSuccess('Ordem dos módulos atualizada com sucesso!')
        } catch (error) {
            console.error('Erro ao atualizar ordem dos módulos:', error)
            showError('Erro ao atualizar ordem dos módulos. Tente novamente.')
            // Recarregar lista original em caso de erro
            loadModulos()
        }
    }

    useEffect(() => {
        if (id) {
            loadCurso()
            loadModulos()
        }
    }, [id])

    const loadCurso = async () => {
        try {
            const { data, error } = await supabase
                .from('cursos')
                .select('id, titulo, descricao')
                .eq('id', id)
                .single()

            if (error) throw error
            setCurso(data)
        } catch (err) {
            console.error('Erro ao carregar curso:', err)
            showError('Erro ao carregar informações do curso.')
        }
    }

    const loadModulos = async () => {
        try {
            setLoading(true)
            const { data, error } = await supabase
                .from('modulos')
                .select('*')
                .eq('curso_id', id)
                .order('ordem_exibicao')

            if (error) throw error

            // Buscar contagem de aulas para cada módulo
            const modulosIds = (data || []).map(modulo => modulo.id)
            
            if (modulosIds.length > 0) {
                // Buscar todas as aulas dos módulos deste curso
                const { data: aulasData, error: aulasError } = await supabase
                    .from('aulas')
                    .select('modulo_id')
                    .in('modulo_id', modulosIds)

                if (aulasError) {
                    console.error('Erro ao contar aulas:', aulasError)
                }

                // Contar aulas por módulo
                const contagemAulas: Record<string, number> = {}
                if (aulasData) {
                    aulasData.forEach(aula => {
                        contagemAulas[aula.modulo_id] = (contagemAulas[aula.modulo_id] || 0) + 1
                    })
                }

                // Processar dados para incluir contador de aulas
                const modulosComContadores = (data || []).map(modulo => ({
                    ...modulo,
                    total_aulas: contagemAulas[modulo.id] || 0
                }))

                setModulos(modulosComContadores)
            } else {
                setModulos([])
            }
        } catch (err) {
            console.error('Erro ao carregar módulos:', err)
            showError('Erro ao carregar módulos. Tente novamente.')
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = async () => {
        if (!moduloToDelete) return

        try {
            const { error } = await supabase
                .from('modulos')
                .delete()
                .eq('id', moduloToDelete.id)

            if (error) throw error

            showSuccess('Módulo excluído com sucesso!')
            loadModulos()
        } catch (err) {
            console.error('Erro ao excluir módulo:', err)
            showError('Erro ao excluir módulo. Tente novamente.')
        } finally {
            setIsDeleteModalOpen(false)
            setModuloToDelete(null)
        }
    }

    const openDeleteModal = (modulo: Modulo) => {
        setModuloToDelete(modulo)
        setIsDeleteModalOpen(true)
    }

    const handleEditModulo = (modulo: Modulo) => {
        setEditingModulo(modulo)
        setIsModalOpen(true)
    }

    const closeDeleteModal = () => {
        setIsDeleteModalOpen(false)
        setModuloToDelete(null)
    }

    // Fechar dropdown ao clicar fora
    useEffect(() => {
        const handleClickOutside = () => {
            if (openDropdown) {
                setOpenDropdown(null)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [openDropdown])

    const filteredModulos = modulos.filter(modulo =>
        modulo.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (modulo.descricao && modulo.descricao.toLowerCase().includes(searchTerm.toLowerCase()))
    )

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-8"></div>
                        <div className="space-y-4">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
                                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2"></div>
                                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    if (!curso) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="text-center py-16">
                        <BookOpen className="mx-auto h-24 w-24 text-gray-400 dark:text-gray-500 mb-6" />
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                            Curso não encontrado
                        </h1>
                        <Button
                            onClick={() => navigate('/admin/cursos')}
                            variant="btn_secondary"
                            className="px-8 py-3"
                        >
                            Voltar aos Cursos
                        </Button>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <Button
                        onClick={() => navigate('/admin/cursos')}
                        variant="btn_secondary"
                        className="mb-4 flex items-center gap-2"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Voltar aos Cursos
                    </Button>

                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                                Módulos do Curso
                            </h1>
                            <p className="text-gray-600 dark:text-gray-400">
                                {curso.titulo}
                            </p>
                        </div>
                        <Button
                            onClick={() => {
                                setEditingModulo(null)
                                setIsModalOpen(true)
                            }}
                            variant="btn_add"
                            className="flex items-center gap-2"
                        >
                            <Plus size={20} />
                            Novo Módulo
                        </Button>
                    </div>
                </div>

                {/* Filtros */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />
                        <input
                            type="text"
                            placeholder="Buscar módulos..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 pr-4 py-2 w-full border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                        />
                    </div>
                </div>

                {/* Lista de Módulos */}
                {filteredModulos.length === 0 ? (
                    <div className="text-center py-16">
                        <BookOpen className="mx-auto h-24 w-24 text-gray-400 dark:text-gray-500 mb-6" />
                        <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 mb-2">
                            Nenhum módulo encontrado
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-8">
                            {searchTerm
                                ? 'Tente ajustar os termos de busca.'
                                : 'Crie seu primeiro módulo para começar.'
                            }
                        </p>
                        {!searchTerm && (
                            <Button
                                onClick={() => {
                                    setEditingModulo(null)
                                    setIsModalOpen(true)
                                }}
                                variant="btn_add"
                                className="px-8 py-3"
                            >
                                Criar Primeiro Módulo
                            </Button>
                        )}
                    </div>
                ) : (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext
                            items={filteredModulos.map(modulo => modulo.id)}
                            strategy={verticalListSortingStrategy}
                        >
                            <div className="space-y-4">
                                {filteredModulos.map((modulo) => (
                                    <SortableModuloItem
                                        key={modulo.id}
                                        modulo={modulo}
                                        navigate={navigate}
                                        cursoId={id!}
                                        activeModuloId={activeModuloId}
                                        onEdit={handleEditModulo}
                                        onDelete={openDeleteModal}
                                        openDropdown={openDropdown}
                                        setOpenDropdown={setOpenDropdown}
                                    />
                                ))}
                            </div>
                        </SortableContext>
                    </DndContext>
                )}

                {/* Modal de Exclusão */}
                {isDeleteModalOpen && moduloToDelete && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
                            <div className="p-6">
                                <div className="flex items-center mb-4">
                                    <div className="flex-shrink-0 w-10 h-10 mx-auto bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                                        <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
                                    </div>
                                </div>
                                <div className="text-center">
                                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                                        Excluir Módulo
                                    </h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                                        Tem certeza que deseja excluir o módulo <strong>"{moduloToDelete.titulo}"</strong>?
                                        Esta ação não pode ser desfeita e excluirá todas as aulas associadas.
                                    </p>
                                    <div className="flex gap-3 justify-center">
                                        <Button
                                            variant="btn_secondary"
                                            onClick={closeDeleteModal}
                                        >
                                            Cancelar
                                        </Button>
                                        <Button
                                            variant="btn_secondary"
                                            onClick={handleDelete}
                                            className="bg-red-600 hover:bg-red-700 text-white"
                                        >
                                            Excluir
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal de Módulo */}
                <ModalModulo
                    isOpen={isModalOpen}
                    onClose={() => {
                        setIsModalOpen(false)
                        setEditingModulo(null)
                    }}
                    onSuccess={() => {
                        loadModulos()
                        setIsModalOpen(false)
                        setEditingModulo(null)
                    }}
                    modulo={editingModulo}
                    cursoId={id!}
                />
            </div>
        </div>
    )
}
